import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContentDisplayVariant, ExerciseSubmission } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { ExerciseType } from '@org/model';
import type { MultipleChoiceEvaluationDetail } from '@org/utils-client';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ContentDescription } from '../shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { OptionGroup, OptionRow, WorkbookPanel, ZineButton } from '../../zine';
import {
  splitWorkbookFeedback,
  WorkbookAttemptText,
  WorkbookExtraFeedback,
  WorkbookVerdict,
} from './zine-workbook';

interface MultipleChoiceExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

function optionKeyLabel(index: number): string {
  return index < 26 ? String.fromCharCode(65 + index) : String(index + 1);
}

export function MultipleChoiceExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: MultipleChoiceExerciseProps) {
  void variant; // the workbook panel is the single presentation for this exercise
  const { t } = useTranslation('p2e');
  const options = content.options ?? [];

  // Determine if this is a single-select exercise (only 1 correct answer)
  const isSingleSelect = useMemo(() => {
    const correctCount = options.filter((option) => option.isCorrect).length;
    return correctCount === 1;
  }, [options]);

  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.MultipleChoice) {
      return submission as {
        selectedOptionIds?: string[];
        selectedOptionIndices?: number[];
        selectedOptionId?: string | null;
        selectedOptionIndex?: number | null;
      };
    }
    return null;
  }, [content.lastSubmission]);

  const initialSelection = useMemo(() => {
    if (!lastSubmission) {
      return [];
    }

    const selectionSet = new Set<number>();
    const addSelection = (index: number | null | undefined) => {
      if (
        typeof index === 'number' &&
        Number.isInteger(index) &&
        index >= 0 &&
        index < options.length &&
        !selectionSet.has(index)
      ) {
        selectionSet.add(index);
      }
    };

    (lastSubmission.selectedOptionIndices ?? []).forEach(addSelection);
    addSelection(lastSubmission.selectedOptionIndex);

    const optionIds = [
      ...(lastSubmission.selectedOptionIds ?? []),
      lastSubmission.selectedOptionId ?? undefined,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);

    optionIds.forEach((optionId) => {
      const matchIndex = options.findIndex((option) => option.optionId === optionId);
      if (matchIndex >= 0) {
        addSelection(matchIndex);
      }
    });

    return Array.from(selectionSet).sort((a, b) => a - b);
  }, [lastSubmission, options]);

  const autoEvaluationVariant = useMemo(() => {
    const autoFeedback = feedback?.find((message) => message.source === 'auto');
    return autoFeedback?.variant ?? null;
  }, [feedback]);

  type OptionEvaluation = {
    index: number;
    optionId?: string;
    label?: string;
    isCorrectAnswer: boolean;
    isSelected: boolean;
    isCorrectSelection: boolean;
  };

  type MultipleChoiceDetail = MultipleChoiceEvaluationDetail & {
    optionEvaluations?: OptionEvaluation[];
  };

  const evaluationDetails = useMemo(() => {
    const evaluationPayload =
      (content as StudentExerciseContent & { lastEvaluation?: Record<string, unknown> | null }).lastEvaluation as
        | { auto?: { details?: MultipleChoiceDetail } }
        | null
        | undefined;
    const detail = evaluationPayload?.auto?.details;
    if (detail && detail.kind === 'MultipleChoice') {
      return detail;
    }
    return null;
  }, [content]);

  const optionEvaluationMap = useMemo(() => {
    if (!evaluationDetails?.optionEvaluations) {
      return null;
    }
    const map = new Map<number, OptionEvaluation>();
    evaluationDetails.optionEvaluations.forEach((option) => {
      map.set(option.index, option);
    });
    return map;
  }, [evaluationDetails]);

  const [selectedOptions, setSelectedOptions] = useState<number[]>(initialSelection);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedSet = useMemo(() => new Set(selectedOptions), [selectedOptions]);

  useEffect(() => {
    setSelectedOptions(initialSelection);
  }, [content.id, initialSelection]);

  const toggleOption = (index: number) => {
    if (isReadOnly) {
      return;
    }
    setSelectedOptions((previous) => {
      if (isSingleSelect) {
        // Single-select mode: clicking the same option deselects it, otherwise select only the new one
        if (previous.includes(index)) {
          return [];
        }
        return [index];
      }
      // Multi-select mode: toggle the option
      if (previous.includes(index)) {
        return previous.filter((value) => value !== index);
      }
      return [...previous, index].sort((a, b) => a - b);
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      const selectedOptionIds = selectedOptions
        .map((index) => options[index]?.optionId)
        .filter((value): value is string => typeof value === 'string' && value.length > 0);
      await onExerciseSubmit(
        content,
        {
          type: ExerciseType.MultipleChoice,
          selectedOptionIndices: selectedOptions,
          selectedOptionIds,
        } as ExerciseSubmission,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Maps existing evaluation/selection state onto the zine option states. */
  const resolveOptionState = (index: number): 'default' | 'picked' | 'right' => {
    const isSelected = selectedSet.has(index);
    const optionEvaluation = optionEvaluationMap?.get(index);

    if (optionEvaluation) {
      if (optionEvaluation.isSelected && optionEvaluation.isCorrectSelection) {
        return 'right';
      }
      if (!optionEvaluation.isSelected && optionEvaluation.isCorrectAnswer) {
        // reveal the correct answer after grading (mirrors the previous UI)
        return 'right';
      }
      if (optionEvaluation.isSelected) {
        return 'picked';
      }
      return 'default';
    }

    if (isSelected && autoEvaluationVariant === 'success') {
      return 'right';
    }
    return isSelected ? 'picked' : 'default';
  };

  const { verdictMessage, extraMessages } = splitWorkbookFeedback(feedback);

  const panelTitle = content.title
    ? renderInlineMarkdown(content.title, 'multiple-choice-title')
    : content.question
      ? renderInlineMarkdown(content.question, 'multiple-choice-question')
      : undefined;
  const showQuestionLine = Boolean(content.title && content.question);

  return (
    <form onSubmit={handleSubmit}>
      <WorkbookPanel
        tab={t('zine.workbook.tab.autoChecked')}
        title={panelTitle}
        hint={t(isSingleSelect ? 'zine.workbook.chooseOne' : 'zine.workbook.chooseMany')}
        footer={
          <>
            {onExerciseSubmit && !isReadOnly && (
              <ZineButton
                type="submit"
                isLoading={isSubmitting}
                disabled={selectedOptions.length === 0 || isSubmitting}
              >
                {isSubmitting ? t('zine.workbook.submitting') : t('zine.workbook.check')}
              </ZineButton>
            )}
            {!isReadOnly && <WorkbookAttemptText content={content} />}
            <WorkbookVerdict pending={isSubmitting} message={verdictMessage} />
            <WorkbookExtraFeedback pending={isSubmitting} messages={extraMessages} />
          </>
        }
      >
        <ContentDescription text={content.description} markdown className="mb-3" />
        {showQuestionLine && content.question && (
          <p className="mb-3 whitespace-pre-line text-[.95rem] font-semibold">
            {renderInlineMarkdown(content.question, 'multiple-choice-question')}
          </p>
        )}
        <OptionGroup>
          {options.map((option, index) => {
            const optionId = option.optionId ?? `${content.id}-option-${index}`;
            const state = resolveOptionState(index);
            return (
              <OptionRow
                key={optionId}
                keyLabel={optionKeyLabel(index)}
                state={state}
                disabled={isReadOnly}
                aria-label={`Selecionar ${option.label || `opção ${index + 1}`}`}
                onClick={() => toggleOption(index)}
                className={isReadOnly ? 'cursor-default opacity-80' : undefined}
              >
                <span className="flex-1">
                  {renderInlineMarkdown(
                    option.label || `Option ${index + 1}`,
                    `${content.id}-option-${index}`,
                  )}
                </span>
              </OptionRow>
            );
          })}
        </OptionGroup>
      </WorkbookPanel>
    </form>
  );
}
